<template>
  <base-layout>
    <div class="page-single position-relative">
      <b-container
        class="register-content"
        fluid
      >
        <b-row
          class="justify-content-center mt-9 mb-6"
        >
          <b-col
            cols="12"
          >
            <div
              class="text-center mb-6"
            >
              <img
                alt="logo"
                class="h-6"
                width="240"
                src="/images/smartmatch-logo.png"
              >
            </div>
          </b-col>
          <b-col
            cols="12"
            lg="6"
            md="8"
            order="2"
            order-md="1"
            xl="4"
          >
            <b-form
              @submit.prevent="signup"
              class="card"
              data-test="register-form"
            >
              <div class="card-body p-6">
                <div
                  data-test="register-title"
                >
                  <h4 class="card-title">
                    Sign up for SmartMatch
                  </h4>
                  <small class="card-black-subtitle">By creating an account, you are under no obligation, and will incur no costs unless you enter
                    invoicing details and submit your campaign for approval.</small>
                </div>
                <b-row class="justify-content-center mt-6">
                  <b-col
                    cols="12"
                    md="6"
                    order="2"
                    order-md="1"
                  >
                    <b-form-group
                      id="first-name-group"
                      label="First name*"
                      label-class="form-label"
                      label-for="fist-name"
                    >
                      <b-form-input
                        :maxlength="maxNameLength"
                        autocomplete="off"
                        autofocus
                        data-test="first-name"
                        id="fist-name"
                        placeholder="First name"
                        required
                        v-model="firstName"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="6"
                    order="2"
                    order-md="1"
                  >
                    <b-form-group
                      id="last-name-group"
                      label="Last name*"
                      label-class="form-label"
                      label-for="last-name"
                    >
                      <b-form-input
                        :maxlength="maxNameLength"
                        autocomplete="off"
                        autofocus
                        data-test="last-name"
                        id="last-name"
                        placeholder="Last name"
                        required
                        v-model="lastName"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-form-group
                  id="email-address-group"
                  label="Email address*"
                  label-class="form-label"
                  label-for="email"
                >
                  <b-form-input
                    autocomplete="email"
                    autofocus
                    data-test="email-input"
                    id="email"
                    maxlength="120"
                    name="email"
                    placeholder="Email address"
                    required
                    type="email"
                    v-model="email"
                    @blur.native="onBlurEmail"
                    :class="[emailValid ? '' : 'is-invalid']"
                  />
                  <p
                    v-if="!emailValid"
                    class="invalid small"
                  >
                    Please enter a valid email address
                  </p>
                </b-form-group>
                <b-form-group
                  id="company-name-group"
                  label="Company*"
                  label-class="form-label"
                  label-for="company-name"
                >
                  <b-form-input
                    :maxlength="maxCompanyName"
                    autocomplete="off"
                    autofocus
                    data-test="company-name"
                    id="company-name"
                    placeholder="Company name"
                    required
                    v-model="companyName"
                  />
                </b-form-group>
                <div class="password-info">
                  <b-form-group
                    id="password-group"
                    label="Password*"
                    label-class="form-label"
                    label-for="password"
                  >
                    <b-form-input
                      autocomplete="off"
                      data-test="password-input"
                      id="password"
                      name="password"
                      placeholder="Password"
                      required
                      type="password"
                      v-model="password"
                      @blur.native="onBlurPassword"
                      :class="[password && !passwordMeetsRequirements ? 'is-invalid' : '']"
                    />
                    <i
                      class="fa-light fa-info-circle"
                      id="password-hint"
                    />
                    <b-tooltip
                      target="password-hint"
                      placement="right"
                      variant="light"
                      trigger="hover focus click"
                    >
                      <ul class="dashed text-left">
                        <li>Password requirements:</li>
                        <li>Minimum length of 8 characters</li>
                        <li>At least 1 number</li>
                        <li>
                          At least 1 special character from this set:<br>
                          ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \ , > ' : ; | _ ~ `
                        </li>
                        <li>At least 1 uppercase letter</li>
                        <li>At least 1 lowercase letter</li>
                      </ul>
                    </b-tooltip>
                    <p
                      v-if="password && !passwordMeetsRequirements"
                      class="invalid small"
                    >
                      Supplied password does not meet the requirements
                    </p>
                  </b-form-group>
                </div>
                <b-form-group
                  id="repeat-password-group"
                  label="Repeat password*"
                  label-class="form-label"
                  label-for="repeat-password"
                >
                  <b-form-input
                    autocomplete="off"
                    data-test="repeat-password-input"
                    id="repeat-password"
                    name="repeatPassword"
                    placeholder="Password"
                    required
                    type="password"
                    v-model="repeatPassword"
                    @blur.native="passwordsMatch = password === repeatPassword"
                    :class="[repeatPassword && !passwordsMatch ? 'is-invalid' : '']"
                  />
                  <p
                    v-if="repeatPassword && !passwordsMatch"
                    class="invalid small"
                  >
                    Passwords do not match
                  </p>
                </b-form-group>

                <b-form-checkbox
                  data-test="terms-checkbox"
                  id="terms-policy"
                  v-model="termsPolicyAccepted"
                >
                  I accept the
                  <a
                    href="https://www.smartology.net/terms-of-service/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >Terms of Service</a> and
                  <a
                    href="https://www.smartology.net/privacy-policy/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >Privacy Policy</a>
                </b-form-checkbox>

                <div class="form-footer">
                  <b-button
                    :class="{ 'btn-loading': isLoading }"
                    :disabled="isDisabled"
                    block
                    data-test="register-submit"
                    id="submit"
                    type="submit"
                    variant="primary"
                  >
                    Create new account
                  </b-button>
                </div>
                <div
                  class="text-center text-muted mt-3"
                >
                  Already have an account?
                  <router-link
                    :to="this.$route.query.redirect ?`login?redirect=${this.$route.query.redirect}`:`login`"
                  >
                    Log in
                  </router-link>
                </div>
              </div>
            </b-form>
          </b-col>
          <b-col
            cols="12"
            order="3"
          >
            <div class="text-center text-muted mt-4">
              Need support? Contact our team <a
                href="mailto:support@smartology.net"
                target="_blank"
              >support@smartology.net</a>
            </div>
          </b-col>
        </b-row>
        <div
          class="header-brand d-flex justify-content-center mb-5 mr-0"
          style="z-index: 0;"
        >
          <a
            target="_blank"
            href="https://www.smartology.net/"
          >
            <img
              src="/images/logo.png"
              class="h-7 header-brand-img mr-0"
              alt="Smartology"
            >
          </a>
        </div>
      </b-container>
    </div>
  </base-layout>
</template>
<script>
import signUtils from '@/core/signUtils';
import { mapActions } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import * as types from '@/modules/authentication/store/types';
import BaseLayout from '@/components/BaseLayout.vue';

export default {
  name: 'Register',
  components: { BaseLayout },
  data: () => ({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    password: '',
    repeatPassword: '',
    termsPolicyAccepted: false,
    maxNameLength: 255,
    maxCompanyName: 120,
    clicked: false,
    isLoading: false,
    emailValid: true,
    passwordsMatch: true,
    passwordMeetsRequirements: true,
  }),
  computed: {
    isDisabled() {
      if (this.isLoading) return true;
      return !this.firstName || !this.lastName || !this.companyName || !this.email
        || !this.password || !this.termsPolicyAccepted || !this.passwordMeetsRequirements || this.password !== this.repeatPassword;
    },
  },
  methods: {
    ...mapActions('authentication', [
      types.SIGN_UP,
    ]),
    onBlurPassword() {
      this.passwordMeetsRequirements = signUtils.doesPasswordMeetRequirements(this.password);
      if (this.repeatPassword) this.passwordsMatch = this.password === this.repeatPassword;
    },
    onBlurEmail() {
      this.emailValid = !this.email || signUtils.isEmailValid(this.email);
    },
    signup() {
      if (this.clicked) return;
      this.clicked = true;
      this.isLoading = true;

      const signUpRequest = {
        firstName: this.firstName,
        lastName: this.lastName,
        companyName: this.companyName,
        email: this.email,
        password: this.password,
        authCode: uuidv4(),
      };

      const { redirect } = this.$route.query;

      if (redirect) {
        signUpRequest.emailProperties = this.constructEmailProperties(redirect);
      }

      this.SIGN_UP(signUpRequest).then(() => {
        this.clicked = false;
        this.isLoading = false;
        this.$router.push(this.$route.query.redirect
          ? `/verify?redirect=${this.$route.query.redirect}`
          : '/verify');
      }).catch(() => {
        this.clicked = false;
        this.isLoading = false;
      });
    },
    constructEmailProperties(redirect) {
      return `{"redirect": "${redirect}"}`;
    },
  },
  mounted() {
    const { email } = this.$route.query;
    this.email = email || '';
  },
};
</script>
<style scoped>
  .register-content {
    max-height: 100vh;
    overflow-y: auto;
  }
  ul {
    margin: 0;
    padding: 10px;
  }
  ul.dashed {
    list-style-type: none;
  }
  ul.dashed > li {
    text-indent: -5px;
  }
  ul.dashed > li:before {
    content: "- ";
    text-indent: -5px;
  }
  ul.dashed > li:first-child:before {
    content: "";
  }
  .password-info {
    position: relative;
    display: inline-block;
    width: 100%;

  }
  .fa-info-circle {
    font-size: 1.50rem;
    top: 35px;
    position: absolute;
    left: auto;
    right: 10px;
  }
</style>
